import { localisateurAffiche } from '../domaine/citations';
// Écran « Architecture » : chaque élément de l'application cible montre son statut (« nouveau » pour un objet de
// l'application, « réutilisé » pour un objet de la carte listé dans `objets_reutilises` de la réponse de l'API) et sa
// citation. Une citation n'est dite « vérifiée » que si l'API l'a vérifiée (elle refuse tout l'essai sinon, 422).
import type { Carte, Citation } from '../api/types';
import { Etiquette } from '../composants/communs';
import { LIBELLE_STATUT_ELEMENT, lignesArchitecture, lignesFlux, pluriel } from '../domaine/applicationCible';
import { lienVers } from '../routeur';
import type { VarianteApplication } from './Application';

function CitationsLigne({ citations, verifiees }: { citations: Citation[]; verifiees: boolean }) {
  if (!citations.length) return <span className="muted">Aucune citation.</span>;
  return (
    <div className="pile" style={{ gap: 6 }}>
      {citations.map((c, i) => (
        <div className="citation" key={`${c.source}-${c.localisateur}-${i}`}>
          <q>{c.extrait}</q>
          <span className="src">
            {c.source} · {localisateurAffiche(c.localisateur)} · {verifiees ? 'vérifiée mot pour mot par l’API' : 'pas encore vérifiée'}
          </span>
        </div>
      ))}
    </div>
  );
}

export function EcranArchitecture({ variante, carte, surCalcul }: { variante: VarianteApplication | undefined; carte: Carte | undefined; surCalcul: () => void }) {
  if (!variante)
    return (
      <p className="encadre info">
        Aucune application cible n’est chargée. <a href={lienVers('application')}>Chargez-en une à l’écran « Besoin »</a>.
      </p>
    );
  const { application, effet } = variante;
  const verifiees = !!effet;
  const lignes = lignesArchitecture(application, effet, carte);
  const flux = lignesFlux(application, carte);
  return (
    <>
      <p className="sous-titre">
        <b>{application.nom}</b>, application proposée, non retenue. Chaque élément affiche les citations que porte le fichier chargé ;
        l’API ne les vérifie mot pour mot qu’au calcul de l’effet.
        {' '}Les localisateurs longs sont tronqués à l’affichage ; les citations sont transmises complètes à l’API.
      </p>
      <p className={`encadre ${verifiees ? 'ok' : 'attention'}`} data-testid="etat-citations">
        {verifiees
          ? `${pluriel(effet.citations_verifiees, 'citation')} ${effet.citations_verifiees > 1 ? 'vérifiées' : 'vérifiée'} mot pour mot par l’API (révision ${effet.revision} de la carte).`
          : 'Les citations ne sont vérifiées par l’API qu’au calcul de l’effet. '}
        {!verifiees && (
          <>
            <button type="button" className="btn petit" onClick={surCalcul} disabled={variante.etat === 'calcul'}>
              Vérifier les citations et lire les objets réutilisés
            </button>
          </>
        )}
      </p>
      <div className="tableau-defile">
        <table aria-label="Éléments de l’application cible">
          <thead>
            <tr>
              <th scope="col">Composant</th>
              <th scope="col">Statut</th>
              <th scope="col">Citation et source</th>
            </tr>
          </thead>
          <tbody>
            {lignes.map((l) => (
              <tr key={`${l.statut}-${l.id}`} data-testid={`ligne-${l.id}`}>
                <th scope="row">
                  {l.nom}
                  <small className="note" style={{ display: 'block' }}>
                    {l.type}
                    {l.type ? ' · ' : ''}
                    <span className="num">{l.id}</span>
                  </small>
                </th>
                <td>
                  <Etiquette ton={l.statut === 'nouveau' ? 'attention' : 'ok'}>{LIBELLE_STATUT_ELEMENT[l.statut]}</Etiquette>
                </td>
                <td>
                  <CitationsLigne citations={l.citations} verifiees={verifiees} />
                  {l.statut === 'reutilise' && l.viaLiens.length > 0 && <small className="note">Citée par les liens de l’application : {l.viaLiens.join(', ')}.</small>}
                </td>
              </tr>
            ))}
            {!verifiees && (
              <tr>
                <td colSpan={3} className="note">
                  Les objets de la carte que l’application réutilise s’affichent après le calcul de l’effet : c’est l’API qui les liste.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <section className="panneau" aria-labelledby="t-flux">
        <h2 id="t-flux">Flux et liens</h2>
        {flux.length === 0 ? (
          <p className="muted">L’application ne propose aucun lien.</p>
        ) : (
          <ul className="liste-points">
            {flux.map((f) => (
              <li key={f.id} className="ok" data-testid={`flux-${f.id}`}>
                <b>{f.de}</b> <span aria-label={f.nature}>→ {f.nature} →</span> <b>{f.vers}</b>
                {f.detail && <small>{f.detail}</small>}
                <CitationsLigne citations={f.citations} verifiees={verifiees} />
              </li>
            ))}
          </ul>
        )}
      </section>
      <section className="panneau" aria-labelledby="t-manque">
        <h2 id="t-manque">Pas encore disponible</h2>
        <p className="note">
          Les données sensibles repérées et les règles de l’entreprise respectées (souveraineté, identité, interdits) ne sont pas
          encore fournies par l’API : elles ne sont donc pas affichées, plutôt qu’écrites de mémoire.
        </p>
      </section>
      <p>
        <a className="btn principal" href={lienVers('application', { etape: 'impact' })}>
          Voir l’impact
        </a>
      </p>
    </>
  );
}
